import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Layers, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { LAYER_OPTIONS } from "./tileLayers";

/**
 * Compact dropdown for switching between base map providers.
 *
 * Fase 2B (b99.10.10) — single source of truth for the map type selector.
 * Substituiu três cópias hard-coded (Controls inline + RouteMapViewer
 * inline + RouteMapViewer fullscreen).
 *
 * b99.10.12 (2026-05-19) — passou de um botão segmentado horizontal
 * (Mapa | Satélite | Terreno | Híbrido) para **dropdown** com botão
 * compacto a mostrar a camada activa. Com 4 opções o segmentado
 * ocupava demasiado espaço na toolbar; o dropdown deixa apenas um
 * botão pequeno com o nome da camada actual + chevron, abrindo as
 * opções sob demanda.
 *
 * Props:
 *  - view         current view id ("2d" | "sat" | "topo" | "hybrid")
 *  - onChange     (id) => void
 *  - testIdPrefix override the data-testid namespace (defaults to
 *                 "map-layer"). Cada opção do menu recebe
 *                 `${prefix}-${id}` para não quebrar selectors antigos.
 *  - variant      "floating" (default, glass card over the map) ou
 *                 "header" (no extra background, p/ fullscreen).
 *  - hideOnMobile when true (default) wraps in `hidden md:flex`; pass
 *                 false to keep visible at all breakpoints.
 */
export default function LayerSelector({
  view,
  onChange,
  testIdPrefix = "map-layer",
  variant = "floating",
  hideOnMobile = true,
  portalContainer,
  bordered = true,
  iconOnly = false,
}) {
  const { t } = useTranslation("common");
  if (!onChange) return null;

  // b99.10.64 (2026-05-22) — resolve the i18n key per option with a
  // graceful fallback to the hardcoded PT label (see tileLayers.js).
  // Doing it inline keeps `tileLayers.js` framework-agnostic.
  const labelOf = (opt) => (opt.labelKey ? t(opt.labelKey, opt.label) : opt.label);
  const current = LAYER_OPTIONS.find((o) => o.id === view) || LAYER_OPTIONS[0];
  const currentLabel = labelOf(current);
  const isHeader = variant === "header";

  const wrapperClass = [
    hideOnMobile ? "hidden md:flex" : "flex",
    "items-center",
  ].join(" ");

  const triggerClass = [
    "inline-flex items-center gap-1.5 rounded-md text-xs font-medium",
    bordered ? "border border-border" : "",
    // b99.10.42 (2026-05-21) — Altura `h-8` (= shadcn Button sm) em
    // ambas variantes para alinhar com os restantes botões da toolbar
    // do mapa (Button sm = h-8, MapVisibilityMenu variant=button).
    // Sem isto a versão floating ficava 4px mais alta do que os
    // vizinhos nos mapas público e de edição.
    "h-8 px-2.5 text-[11px] transition-colors",
    !bordered
      ? "hover:bg-muted text-foreground"
      : isHeader
        ? "bg-background hover:bg-muted text-foreground"
        : "bg-background/90 backdrop-blur-sm hover:bg-muted text-foreground",
    // b99.10.40 (2026-05-21) — Anel laranja `focus-visible` removido a
    // pedido do utilizador: ficava persistente quando o trigger
    // mantinha foco após o utilizador alterar uma opção, parecendo um
    // border permanente. Mantemos apenas o `outline-none` para evitar
    // o anel azul nativo do browser.
    "focus:outline-none focus-visible:outline-none focus-visible:ring-0",
  ].join(" ");

  return (
    <div className={wrapperClass} data-testid={`${testIdPrefix}-selector`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={iconOnly ? "hover:bg-muted rounded-lg p-1.5 sm:p-2 text-foreground focus:outline-none focus-visible:ring-0" : triggerClass}
            data-testid={`${testIdPrefix}-trigger`}
            aria-label={t("map.layer.aria", { label: currentLabel, defaultValue: `Camada do mapa: ${currentLabel}` })}
            title={t("map.layer.aria", { label: currentLabel, defaultValue: `Camada do mapa: ${currentLabel}` })}
          >
            {iconOnly ? (
              <Layers className="h-4 w-4" />
            ) : (
              <>
                <Layers className="h-3.5 w-3.5 opacity-70" />
                <span>{currentLabel}</span>
                <ChevronDown className="h-3 w-3 opacity-70" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="min-w-[130px] z-[10000] border-2 border-zinc-600 shadow-2xl shadow-black/50"
          portalContainer={portalContainer}
        >
          <DropdownMenuRadioGroup value={view} onValueChange={onChange}>
            {LAYER_OPTIONS.map((opt) => (
              <DropdownMenuRadioItem
                key={opt.id}
                value={opt.id}
                data-testid={`${testIdPrefix}-${opt.id}`}
                className="text-xs cursor-pointer"
              >
                {/* O `DropdownMenuRadioItem` da shadcn já renderiza
                    um indicator à esquerda; mantemos o label simples. */}
                <span>{labelOf(opt)}</span>
                {view === opt.id && (
                  <Check className="h-3.5 w-3.5 ml-auto text-orange-500" />
                )}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
